import { CartUpdateEvent } from '@theme/events';
import { CartMutationQueue, reconcileGift, resolveCartChange, rewardProgress } from '@theme/alvorn-gift-policy';

const configElement = document.getElementById('alvorn-free-gift-config');

if (configElement && !window.alvornGiftPromotion) {
  const config = JSON.parse(configElement.textContent);
  const source = 'alvorn-free-gift';
  const nativeFetch = window.fetch.bind(window);
  const root = window.Shopify?.routes?.root ?? '/';
  const money = new Intl.NumberFormat(document.documentElement.lang || 'tr', { style: 'currency', currency: 'TRY', maximumFractionDigits: 2 });
  let active = null;
  let rerun = false;
  let lastResult = null;
  let checkoutPending = false;
  let allowSubmit = false;
  let externalSequence = 0;
  let quantityIntentUntil = 0;
  let quantityIntentTimer = null;
  const itemHistory = new Map();
  const rememberItems = (cart) => cart?.items?.forEach((item) => itemHistory.set(item.key, item));

  const cards = () => document.querySelectorAll('[data-alvorn-gift]');
  const busy = (pending) => {
    const processing = pending > 0 || checkoutPending || Date.now() < quantityIntentUntil;
    document.querySelectorAll('.cart__checkout-button').forEach((button) => {
      button.toggleAttribute('aria-busy', processing);
    });
    document.querySelectorAll('.additional-checkout-buttons').forEach((element) => {
      element.inert = processing || lastResult?.safe !== true;
    });
  };
  const queue = new CartMutationQueue(busy);

  const paint = (result) => {
    if (!result) return;
    const state = result.state;
    const rewards = rewardProgress(result.cart, config, state);
    const message = (config.messages[state.status] ?? config.messages.error).replace('__remaining__', money.format(state.remaining / 100));
    cards().forEach((card) => {
      card.dataset.state = state.status;
      card.dataset.shippingUnlocked = String(rewards.shippingUnlocked);
      card.dataset.giftUnlocked = String(rewards.giftUnlocked);
      const shippingStatus = card.querySelector('[data-shipping-status]');
      const shippingMessage = (rewards.shippingUnlocked ? config.shippingMessages?.unlocked : config.shippingMessages?.progress)?.replace('__remaining__', money.format(rewards.shippingRemaining / 100));
      if (shippingStatus && shippingMessage && shippingStatus.textContent !== shippingMessage) shippingStatus.textContent = shippingMessage;
      const status = card.querySelector('[data-gift-status]');
      if (status && status.textContent !== message) status.textContent = message;
      const progress = card.querySelector('[data-gift-progress]');
      if (progress) {
        progress.value = rewards.fill;
        progress.hidden = false;
        progress.setAttribute('aria-label', [shippingMessage, message].filter(Boolean).join(' '));
      }
      const shop = card.querySelector('[data-gift-shop]');
      if (shop) shop.hidden = state.status !== 'progress';
    });
    busy(queue.pending);
  };

  const request = async (path, options = {}) => {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 10000);
    try {
      const response = await nativeFetch(`${root}${path}`, {
        credentials: 'same-origin',
        cache: 'no-store',
        ...options,
        signal: controller.signal,
      });
      const data = await response.json();
      if (!response.ok || data.errors || data.status >= 400) throw new Error('Cart request failed');
      return data;
    } finally {
      clearTimeout(timer);
    }
  };

  const sectionIds = () => [...new Set([...document.querySelectorAll('cart-items-component[data-section-id]')]
    .map((element) => element.dataset.sectionId).filter(Boolean))].slice(0, 5);

  const transport = {
    read: async () => {
      const cart = await request('cart.js');
      rememberItems(cart);
      return cart;
    },
    mutate: async (kind, payload) => {
      const response = await request(`cart/${kind}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ ...payload, sections: sectionIds(), sections_url: window.location.pathname }),
      });
      return response;
    },
  };

  const reconcile = () => {
    if (active) {
      rerun = true;
      return active;
    }
    active = queue.run(async () => {
      const result = await reconcileGift(transport, config, {
        suppressAdd: lastResult?.state.status === 'discount-unavailable' && lastResult.sequence === externalSequence,
      });
      result.sequence = externalSequence;
      lastResult = result;
      if (result.changed && result.cart) {
        document.dispatchEvent(new CartUpdateEvent(result.cart, source, { source, sections: result.sections }));
      }
      paint(result);
      return result;
    }).finally(() => {
      active = null;
      if (rerun) {
        rerun = false;
        reconcile();
      }
    });
    return active;
  };

  window.fetch = (input, options) => {
    const url = new URL(typeof input === 'string' || input instanceof URL ? input : input.url, window.location.href);
    const method = (options?.method ?? input?.method ?? 'GET').toUpperCase();
    const mutation = url.origin === window.location.origin && method !== 'GET' && /\/cart\/(add|change|update|clear)(\.js)?\/?$/.test(url.pathname);
    if (!mutation) return nativeFetch(input, options);
    let linePayload = null;
    let intendedItem = null;
    if (/\/cart\/change(\.js)?\/?$/.test(url.pathname) && typeof options?.body === 'string') {
      try {
        const payload = JSON.parse(options.body);
        if (Number.isInteger(payload.line)) {
          const component = document.activeElement?.closest?.('cart-items-component') ?? document.querySelector('cart-items-component');
          const key = component?.querySelectorAll('[ref="cartItemRows[]"][data-key]')[payload.line - 1]?.dataset.key;
          intendedItem = itemHistory.get(key) ?? (key ? { key } : null);
          linePayload = payload;
        }
      } catch (_) {}
    }
    return queue.run(async () => {
      const timeout = new AbortController();
      const timer = setTimeout(() => timeout.abort(), 10000);
      const signal = options?.signal ?? input?.signal;
      try {
        signal?.throwIfAborted();
        let body = options?.body;
        if (linePayload) {
          const cart = await request('cart.js');
          rememberItems(cart);
          body = JSON.stringify(resolveCartChange(linePayload, intendedItem, cart));
          signal?.throwIfAborted();
        }
        const response = await nativeFetch(input, { ...options, body, signal: signal ? AbortSignal.any([signal, timeout.signal]) : timeout.signal });
        if (response.ok) {
          try {
            const data = await response.clone().json();
            rememberItems(data);
          } catch (_) {}
        }
        return response;
      } finally {
        clearTimeout(timer);
        externalSequence += 1;
        queueMicrotask(() => reconcile());
      }
    });
  };

  const onCartUpdate = (event) => {
    if (event.detail?.sourceId === source || event.detail?.data?.source === source) return;
    externalSequence += 1;
    rememberItems(event.detail?.resource);
    reconcile();
  };
  document.addEventListener('cart:update', onCartUpdate);
  document.addEventListener('discount:update', onCartUpdate);

  document.addEventListener('quantity-selector:update', (event) => {
    if (!event.target.closest?.('cart-items-component') || !Number.isInteger(event.detail?.cartLine)) return;
    quantityIntentUntil = Date.now() + 350;
    clearTimeout(quantityIntentTimer);
    quantityIntentTimer = setTimeout(() => busy(queue.pending), 350);
    busy(queue.pending);
  });

  const waitForQuantityIntent = async () => {
    while (Date.now() < quantityIntentUntil) {
      await new Promise((resolve) => setTimeout(resolve, quantityIntentUntil - Date.now()));
    }
  };

  const continueCheckout = async (form, button) => {
    if (checkoutPending) return;
    checkoutPending = true;
    busy(queue.pending);
    try {
      await waitForQuantityIntent();
      await reconcile();
      while (active || queue.pending > 0 || Date.now() < quantityIntentUntil) {
        await waitForQuantityIntent();
        if (active) await active;
        else if (queue.pending > 0) await queue.run(async () => {});
      }
      if (lastResult?.safe !== true) {
        cards().forEach((card) => {
          card.dataset.state = 'error';
          const status = card.querySelector('[data-gift-status]');
          if (status) status.textContent = config.messages['checkout-error'];
        });
        button?.focus();
        return;
      }
      allowSubmit = true;
      const submitter = button?.isConnected ? button : document.querySelector('.cart__checkout-button');
      if (submitter?.form === form) form.requestSubmit(submitter);
      else if (form.isConnected) {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = 'checkout';
        input.value = 'checkout';
        form.append(input);
        form.requestSubmit();
        input.remove();
      } else window.location.assign(`${root}checkout`);
    } finally {
      allowSubmit = false;
      checkoutPending = false;
      busy(queue.pending);
    }
  };

  document.addEventListener('click', (event) => {
    const button = event.target.closest?.('button[name="checkout"]');
    if (!button || allowSubmit) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    if (button.form) continueCheckout(button.form, button);
  }, true);
  document.addEventListener('submit', (event) => {
    if (allowSubmit || event.target.id !== 'cart-form') return;
    const button = event.submitter ?? document.querySelector('.cart__checkout-button');
    if (button?.name !== 'checkout') return;
    event.preventDefault();
    event.stopImmediatePropagation();
    continueCheckout(event.target, button);
  }, true);
  document.addEventListener('click', (event) => {
    if (event.target.closest?.('[data-testid="cart-drawer-trigger"]')) paint(lastResult);
  });
  new MutationObserver(() => paint(lastResult)).observe(document.body, { childList: true, subtree: true });
  window.addEventListener('pageshow', () => reconcile());
  window.alvornGiftPromotion = { reconcile };
  reconcile();
}

